import React, { useState, useEffect } from "react";
import { Checkbox, Col, Form, Row, Table } from "antd";

import { useFetchConstant } from "apis/constant";

import FormDatePicker from "components/FormDatePicker/FormDatePicker";
import FormInput from "components/FormInput/FormInput";
import FormPolicySuggestions from "components/FormPolicySuggestions/FormPolicySuggestions";
import FormSelection from "components/FormSelection/FormSelection";
import FormToggle from "components/FormToggle/FormToggle";
import SectionCard from "components/SectionCard/SectionCard";
import SectionContainer from "components/SectionContainer/SectionContainer";
import Title from "components/Title/Title";
import Locale from "react-intl-universal";

const { Item: FormItem } = Form;

const PlanDetails = ({
  isNew,
  selectedType,
  selectedCompany,
  updateSelectedSuggestedPolicy,
  updateSelectedType,
  updateSelectedCompany,
}) => {
  const {
    isLoading: isPlanTypesLoading,
    selection: planTypes,
  } = useFetchConstant("planTypes");
  const {
    isLoading: isInsuranceCompaniesLoading,
    selection: insuranceCompanies,
    data: insuranceCompaniesConst,
  } = useFetchConstant("insuranceCompanies");
  const {
    isLoading: isPolicyStatusesLoading,
    selection: policyStatuses,
  } = useFetchConstant("policyStatuses");
  const {
    isLoading: isBenefitTypesLoading,
    selection: benefitTypes,
  } = useFetchConstant("benefitTypes");
  const {
    isLoading: isPaymentModesLoading,
    selection: paymentModes,
  } = useFetchConstant("paymentModes");
  const isConstantsLoading =
    isPlanTypesLoading ||
    isInsuranceCompaniesLoading ||
    isPolicyStatusesLoading ||
    isBenefitTypesLoading ||
    isPaymentModesLoading;

  const [
    shouldShowOtherCompanyInput,
    setShouldShowOtherCompanyInput,
  ] = useState(false);

  useEffect(() => {
    if (
      !isInsuranceCompaniesLoading &&
      selectedCompany === insuranceCompaniesConst.OTHERS.code
    ) {
      setShouldShowOtherCompanyInput(true);
    } else {
      setShouldShowOtherCompanyInput(false);
    }
  }, [isInsuranceCompaniesLoading, insuranceCompaniesConst, selectedCompany]);

  return (
    <>
      <SectionContainer>
        <SectionCard
          loading={isConstantsLoading}
          title={Locale.get("ownedPolicies.newPolicyForm.sectionCardTitle_0")}
        >
          <Row gutter={16}>
            <Col span={24} lg={12}>
              {!isNew ? (
                <FormInput
                  label={Locale.get("ownedPolicies.newPolicyForm.policyName")}
                  name="name"
                  placeholder={Locale.get(
                    "ownedPolicies.newPolicyForm.policyNamePlaceholder"
                  )}
                  requiredErrorMessage={Locale.get(
                    "ownedPolicies.newPolicyForm.errMsgPolicyName"
                  )}
                />
              ) : (
                <FormPolicySuggestions
                  onSelectPolicy={(selectedPolicy) =>
                    updateSelectedSuggestedPolicy(selectedPolicy)
                  }
                />
              )}
            </Col>
            <Col span={24} lg={12}>
              <FormInput
                label={Locale.get("ownedPolicies.newPolicyForm.policyNo")}
                name="policyNumber"
                placeholder={Locale.get(
                  "ownedPolicies.newPolicyForm.policyNoPlaceholder"
                )}
                requiredErrorMessage={Locale.get(
                  "ownedPolicies.newPolicyForm.errMsgPolicyNo"
                )}
              />
            </Col>
            <Col span={24} lg={12}>
              <FormSelection
                label={Locale.get(
                  "ownedPolicies.newPolicyForm.insuranceCompany"
                )}
                name={["company", "type"]}
                placeholder={Locale.get(
                  "ownedPolicies.newPolicyForm.insurancePlaceholder"
                )}
                requiredErrorMessage={Locale.get(
                  "ownedPolicies.newPolicyForm.errMsgInsurance"
                )}
                selections={insuranceCompanies}
                onChange={updateSelectedCompany}
              />
            </Col>
            {shouldShowOtherCompanyInput && (
              <Col span={24} lg={12}>
                <FormInput
                  label={Locale.get(
                    "ownedPolicies.newPolicyForm.otherInsurance"
                  )}
                  name={["company", "label"]}
                  placeholder={Locale.get(
                    "ownedPolicies.newPolicyForm.otherInsurancePlaceholder"
                  )}
                  requiredErrorMessage={Locale.get(
                    "ownedPolicies.newPolicyForm.errMsgOtherInsurance"
                  )}
                />
              </Col>
            )}
            <Col span={24} lg={12}>
              <FormSelection
                label={Locale.get("ownedPolicies.newPolicyForm.planType")}
                name="type"
                placeholder={Locale.get(
                  "ownedPolicies.newPolicyForm.planTypePlaceholder"
                )}
                requiredErrorMessage={Locale.get(
                  "ownedPolicies.newPolicyForm.errMsgPlanType"
                )}
                selections={planTypes}
                extraProps={{ onChange: updateSelectedType }}
              />
            </Col>
            {selectedType === "otherPlanType" && (
              <Col span={shouldShowOtherCompanyInput ? 12 : 24}>
                <FormInput
                  label={Locale.get("ownedPolicies.newPolicyForm.otherType")}
                  name="otherTypeLabel"
                  placeholder={Locale.get(
                    "ownedPolicies.newPolicyForm.otherTypePlaceholder"
                  )}
                  requiredErrorMessage={Locale.get(
                    "ownedPolicies.newPolicyForm.errMsgOtherType"
                  )}
                />
              </Col>
            )}
            <Col span={12}>
              <FormDatePicker
                label={Locale.get(
                  "ownedPolicies.newPolicyForm.commencementDate"
                )}
                name="commencementDate"
                placeholder="2020-01-01"
              />
            </Col>
            <Col span={12}>
              <FormDatePicker
                label={Locale.get("ownedPolicies.newPolicyForm.maturityDate")}
                name="maturityDate"
                placeholder="2040-01-01"
              />
            </Col>
            <Col span={12}>
              <FormInput
                label={Locale.get("ownedPolicies.newPolicyForm.premium")}
                name="premium"
                placeholder="0.00"
                type="financial"
                requiredErrorMessage={Locale.get(
                  "ownedPolicies.newPolicyForm.errMsgPremium"
                )}
              />
            </Col>
            <Col span={12}>
              <FormSelection
                label={Locale.get("ownedPolicies.newPolicyForm.mop")}
                name="paymentMode"
                placeholder={Locale.get(
                  "ownedPolicies.newPolicyForm.mopPlaceholder"
                )}
                requiredErrorMessage={Locale.get(
                  "ownedPolicies.newPolicyForm.errMsgMop"
                )}
                selections={paymentModes}
              />
            </Col>
            <Col span={12}>
              <FormSelection
                label={Locale.get("ownedPolicies.newPolicyForm.currentStatus")}
                name="status"
                placeholder={Locale.get(
                  "ownedPolicies.newPolicyForm.currentStatusPlaceholder"
                )}
                requiredErrorMessage={Locale.get(
                  "ownedPolicies.newPolicyForm.errMsgCurrentStatus"
                )}
                selections={policyStatuses}
              />
            </Col>
          </Row>
        </SectionCard>
      </SectionContainer>
      <SectionContainer>
        <FormToggle
          label={Locale.get("ownedPolicies.newPolicyForm.formToggleTitle")}
          name="isCoverForOther"
        >
          <SectionCard
            title={Locale.get("ownedPolicies.newPolicyForm.sectionCardTitle")}
          >
            <Row gutter={16}>
              <Col span={24}>
                <FormInput
                  label={Locale.get("ownedPolicies.newPolicyForm.coverLabel")}
                  name="coveredFor"
                  placeholder="Joe Doe"
                  requiredErrorMessage={Locale.get(
                    "ownedPolicies.newPolicyForm.errMsgCoverLabel"
                  )}
                />
              </Col>
              <Col span={24}>
                <Title type="question" marginBottom="24px">
                  {Locale.get("ownedPolicies.newPolicyForm.anyWaiver")}
                </Title>
              </Col>
              <Col span={12} lg={8}>
                <FormItem
                  name={["adviseeCoverages", "deathWaiver"]}
                  valuePropName="checked"
                  style={{ marginTop: "-16px" }}
                >
                  <Checkbox>
                    {Locale.get("ownedPolicies.newPolicyForm.checkBox_a")}
                  </Checkbox>
                </FormItem>
              </Col>
              <Col span={12} lg={8}>
                <FormItem
                  name={["adviseeCoverages", "tpdWaiver"]}
                  valuePropName="checked"
                  style={{ marginTop: "-16px" }}
                >
                  <Checkbox>
                    {Locale.get("ownedPolicies.newPolicyForm.checkBox_b")}
                  </Checkbox>
                </FormItem>
              </Col>
              <Col span={12} lg={8}>
                <FormItem
                  name={["adviseeCoverages", "ciWaiver"]}
                  valuePropName="checked"
                  style={{ marginTop: "-16px" }}
                >
                  <Checkbox>
                    {Locale.get("ownedPolicies.newPolicyForm.checkBox_c")}
                  </Checkbox>
                </FormItem>
              </Col>
            </Row>
          </SectionCard>
        </FormToggle>
      </SectionContainer>
    </>
  );
};

export default PlanDetails;
